import { useInView } from '../../hooks/useInView';

interface MidPageCTAProps {
  headline?: string;
  sub?: string;
  cta?: string;
  image?: string;
  onBook?: () => void;
}

export default function MidPageCTA({
  headline = 'Ready to Begin Your Healing Journey?',
  sub = 'Personalized care. Intentional results. A deeper level of well-being.',
  cta = 'Schedule Your Session',
  image = 'https://public.readdy.ai/ai/img_res/bd8c18a26a6ee759980ae72ec70127bb.jpg',
  onBook,
}: MidPageCTAProps) {
  const { ref, inView } = useInView();

  const handleClick = () => {
    if (onBook) {
      onBook();
      return;
    }
    const el = document.getElementById('booking');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section ref={ref as React.RefObject<HTMLElement>} className="relative py-28 overflow-hidden">
      <div className="absolute inset-0 w-full h-full">
        <img src={image} alt="Khalsa Healing sanctuary" className="w-full h-full object-cover object-top" />
      </div>
      <div className="absolute inset-0 bg-gradient-to-t from-[#080808]/95 via-[#080808]/75 to-[#080808]/60" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#080808]/40 via-transparent to-[#080808]/40" />

      {/* Decorative lines */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#d4af37]/30 to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#d4af37]/30 to-transparent" />

      <div
        className={`relative z-10 max-w-3xl mx-auto px-6 md:px-10 text-center transition-all duration-700 ${
          inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        <div className="flex items-center justify-center gap-4 mb-6">
          <span className="w-16 h-px bg-[#d4af37]/40" />
          <i className="ri-sparkling-2-line text-[#d4af37] text-lg" />
          <span className="w-16 h-px bg-[#d4af37]/40" />
        </div>
        <h2
          className="text-4xl md:text-5xl lg:text-6xl text-white font-light leading-tight mb-6"
          style={{ fontFamily: 'Cormorant Garamond, serif' }}
        >
          {headline}
        </h2>
        <p className="text-[#9a9a9a] text-base mb-12 font-light" style={{ fontFamily: 'Inter, sans-serif' }}>
          {sub}
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button
            onClick={handleClick}
            className="inline-flex items-center justify-center gap-2 bg-[#d4af37] hover:bg-[#c09b2a] text-[#080808] px-12 py-4.5 rounded-full text-xs uppercase tracking-[0.2em] font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            {cta}
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line text-sm" />
            </div>
          </button>
          <a
            href="tel:+12153210305"
            className="inline-flex items-center justify-center gap-2 border border-white/20 hover:border-[#d4af37]/50 text-white/70 hover:text-[#d4af37] px-10 py-4.5 rounded-full text-xs uppercase tracking-[0.15em] transition-all duration-300 cursor-pointer whitespace-nowrap"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-phone-line text-sm" />
            </div>
            Call 215-321-0305
          </a>
        </div>
      </div>
    </section>
  );
}
